import type { RouteObject } from 'react-router-dom';
import {
  EMPLOYEE_PERMISSIONS_VIEW_KEYS,
  PERMISSIONS,
  ROLES_MANAGEMENT_PERMISSIONS,
} from '@shared/constants/permissions';
import type { AppRouteOptions } from '@shared/router/build-routes';
import { lazyPage } from '@shared/router/lazy-page';
import { PATHS } from '@/router/paths';
import { organizationRoutes, crudRoute } from '@/modules/organization/routes';
import { payrollRoutes } from '@/modules/payroll/routes';
import { integrationsRoutes } from '@/modules/integrations/routes';
import {
  EMPLOYEE_PROFILE_TAB_AREA_KEYS,
  EMPLOYEE_RECORD_AREA_META,
  type EmployeeRecordAreaMeta,
} from '@/modules/employees/record-area-meta';

/**
 * HRMS screens. Every route is loaded on demand and carries the permission its
 * backend endpoint requires (`lazyPage` → `PermissionGuard`); the shared tree
 * adds authentication and HRMS application access above them. Hiding a sidebar
 * link is never the protection, and the backend re-checks every request.
 */

/**
 * The profile's tabs ARE routes: each renders the profile with a different tab
 * active, gated by the key that area's controller requires.
 */
const employeeRecordRoutes: RouteObject[] = EMPLOYEE_PROFILE_TAB_AREA_KEYS.map((area) => {
  const definition: EmployeeRecordAreaMeta = EMPLOYEE_RECORD_AREA_META[area];
  return lazyPage(
    PATHS.app.employeeRecord(':employeeId', definition.segment),
    async () => {
      const { EmployeeProfilePage } = await import('@/modules/employees/profile-page');
      return function EmployeeRecordTab() {
        return <EmployeeProfilePage tab={area} />;
      };
    },
    { permission: definition.viewPermission ?? PERMISSIONS.employees.view },
  );
});

const shellRoutes: RouteObject[] = [
  lazyPage(
    PATHS.app.dashboard,
    async () => (await import('@/modules/dashboard/page')).DashboardPage,
    {
      permission: PERMISSIONS.dashboard.view,
    },
  ),

  // ------------------------------------------------------------- people
  lazyPage(
    PATHS.app.employees,
    async () => (await import('@/modules/employees/page')).EmployeesPage,
    {
      permission: PERMISSIONS.employees.view,
    },
  ),
  // Static segments out-rank `/employees/:employeeId`.
  lazyPage(
    PATHS.app.myProfile,
    async () => (await import('@/modules/employees/my-profile-route')).MyProfileRoute,
    { permission: PERMISSIONS.employees.view },
  ),
  lazyPage(
    PATHS.app.employeeNew,
    async () => (await import('@/modules/employees/edit-page')).EmployeeCreatePage,
    { permission: PERMISSIONS.employees.create },
  ),
  lazyPage(
    PATHS.app.employeeProfile(),
    async () => {
      const { EmployeeProfilePage } = await import('@/modules/employees/profile-page');
      return function EmployeeProfileRoute() {
        return <EmployeeProfilePage />;
      };
    },
    { permission: PERMISSIONS.employees.view },
  ),
  // ANY-of, mirroring `PATCH /employees/:id`: finishing a draft is part of creating it.
  lazyPage(
    PATHS.app.employeeEdit(),
    async () => (await import('@/modules/employees/edit-page')).EmployeeEditPage,
    { anyOf: [PERMISSIONS.employees.update, PERMISSIONS.employees.create] },
  ),
  // ANY-of, mirroring `GET /employees/:id/permissions`.
  lazyPage(
    PATHS.app.employeePermissions(),
    async () => {
      const { EmployeePermissionsPage } =
        await import('@shared/domain/access/employee-permissions/employee-permissions-page');
      return function EmployeePermissionsRoute() {
        return (
          <EmployeePermissionsPage backTo={(employeeId) => PATHS.app.employeeProfile(employeeId)} />
        );
      };
    },
    { anyOf: EMPLOYEE_PERMISSIONS_VIEW_KEYS },
  ),
  ...employeeRecordRoutes,

  lazyPage(PATHS.app.users, async () => (await import('@/modules/users/page')).UsersPage, {
    permission: PERMISSIONS.users.view,
  }),
  lazyPage(PATHS.app.userNew, async () => (await import('@/modules/users/new-page')).AddUserPage, {
    permission: PERMISSIONS.users.manage,
  }),
  lazyPage(
    PATHS.app.userDetail(),
    async () => (await import('@/modules/users/detail-page')).UserDetailPage,
    {
      permission: PERMISSIONS.users.view,
    },
  ),

  lazyPage(
    PATHS.app.organization,
    async () => (await import('@/modules/organization/landing-route')).OrganizationLandingRoute,
  ),
  ...organizationRoutes,

  // ------------------------------------------------------- time & leave
  lazyPage(
    PATHS.app.attendance,
    async () => (await import('@/modules/attendance/attendance-page')).AttendancePage,
    { permission: PERMISSIONS.attendance.view },
  ),
  lazyPage(
    PATHS.app.leaveRequests,
    async () => (await import('@/modules/leave/requests-page')).LeaveRequestsPage,
    { permission: PERMISSIONS.leaveRequests.view },
  ),
  lazyPage(
    PATHS.app.leaveBalances,
    async () => (await import('@/modules/leave/balances-page')).LeaveBalancesPage,
    { permission: PERMISSIONS.leaveBalances.view },
  ),
  lazyPage(
    PATHS.app.leavePolicies,
    async () => (await import('@/modules/leave/policies-page')).LeavePoliciesPage,
    { permission: PERMISSIONS.leavePolicies.view },
  ),
  crudRoute(
    PATHS.app.leaveTypes,
    'leave-types',
    async () => (await import('@/modules/organization/leave-types/config')).leaveTypeConfig,
  ),
  lazyPage(
    PATHS.app.holidays,
    async () => (await import('@/modules/holidays/holidays-page')).HolidaysPage,
    {
      permission: PERMISSIONS.holidays.view,
    },
  ),
  lazyPage(
    PATHS.app.separations,
    async () => (await import('@/modules/separations/separations-page')).SeparationsPage,
    { permission: PERMISSIONS.employeeExits.view },
  ),
  lazyPage(
    PATHS.app.separationDetail(),
    async () => (await import('@/modules/separations/separation-detail-page')).SeparationDetailPage,
    { permission: PERMISSIONS.employeeExits.view },
  ),

  // ---------------------------------------------------------------- pay
  ...payrollRoutes,

  // --------------------------------------------------------------- work
  lazyPage(PATHS.app.projects, async () => (await import('@/modules/projects/page')).ProjectsPage, {
    permission: PERMISSIONS.projects.view,
  }),
  crudRoute(
    PATHS.app.projectStatuses,
    'project-statuses',
    async () => (await import('@/modules/projects/setup/configs')).projectStatusConfig,
  ),
  crudRoute(
    PATHS.app.moduleStatuses,
    'module-statuses',
    async () => (await import('@/modules/projects/setup/configs')).moduleStatusConfig,
  ),
  crudRoute(
    PATHS.app.projectRoles,
    'project-roles',
    async () => (await import('@/modules/projects/setup/configs')).projectRoleConfig,
  ),
  ...integrationsRoutes,
  lazyPage(PATHS.app.userLogs, async () => (await import('@/modules/reports/page')).UserLogsPage, {
    permission: PERMISSIONS.reports.view,
  }),
  lazyPage(
    PATHS.app.projectLogs,
    async () => (await import('@/modules/reports/project-logs-page')).ProjectLogsPage,
    { permission: PERMISSIONS.reports.view },
  ),
  lazyPage(
    PATHS.app.exports,
    async () => (await import('@/modules/exports/exports-page')).ExportsPage,
    {
      permission: PERMISSIONS.export.generate,
    },
  ),

  // ------------------------------------------------------------- access
  lazyPage(
    PATHS.app.roles,
    async () => (await import('@shared/domain/access/roles-page')).RolesPage,
    {
      anyOf: [...ROLES_MANAGEMENT_PERMISSIONS, PERMISSIONS.roles.assign],
    },
  ),
  lazyPage(
    PATHS.app.permissions,
    async () => (await import('@shared/domain/access/permissions-page')).PermissionsPage,
    { permission: PERMISSIONS.permissions.view },
  ),

  // -------------------------------------------------------------- audit
  lazyPage(PATHS.app.auditLogs, async () => (await import('@shared/domain/audit/audit-logs-page')).AuditLogsPage, {
    permission: PERMISSIONS.auditLogs.view,
  }),
];

export const hrmsRoutes: AppRouteOptions = { shellRoutes };
